"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getPaidCreditsAtSpendingLimit = getPaidCreditsAtSpendingLimit;
exports.getDollarsSpentFromMixedPriceCredits = getDollarsSpentFromMixedPriceCredits;
exports.getPaidCreditsRemaining = getPaidCreditsRemaining;
exports.hasRemainingPaidCreditsWithMixedPrices = hasRemainingPaidCreditsWithMixedPrices;
exports.getEffectiveSpendingLimit = getEffectiveSpendingLimit;
exports.creditsUsedFromLedgerEntry = creditsUsedFromLedgerEntry;
exports.creditsUsedFromTokensUsed = creditsUsedFromTokensUsed;
exports.totalCreditsUsed = totalCreditsUsed;
exports.getCreditGlobalSettings = getCreditGlobalSettings;
exports.currentCreditPeriod = currentCreditPeriod;
exports.previousCreditPeriod = previousCreditPeriod;
exports.nextCreditPeriod = nextCreditPeriod;
exports.creditPeriodDates = creditPeriodDates;
exports.generateStaticCreditPeriod = generateStaticCreditPeriod;
exports.getPaidCreditConsentRequired = getPaidCreditConsentRequired;
const shared_1 = require("@buildwithflux/shared");
const lodash_1 = require("lodash");
const featureFlags_1 = require("../featureFlags");
const constants_1 = require("../payment/constants");
const structure_1 = require("../payment/structure");
const constants_2 = require("./constants");
const structure_2 = require("./structure");
function getPaidCreditsAtSpendingLimit(spendingLimit, paidCreditPrice) {
    if (!paidCreditPrice) {
        return structure_2.CreditAmount.parse(0);
    }
    return structure_2.CreditAmount.parse(Math.floor(spendingLimit / paidCreditPrice));
}
/**
 * Calculate the total dollars spent (in cents) from credits burned at different prices
 *
 * This function accounts for credits that were burned at different price points (e.g., when a user
 * changes plans mid-period). It sums up the dollar value of each price tier's credit usage.
 *
 * @param paidCreditsUsedByPrice - Map of Stripe price ID to credit amount burned at that price
 * @param paidCreditsUsed - Total paid credits used (fallback for legacy entries without price breakdown)
 * @param currentPaidCreditPriceCents - The current price per credit in cents (used as fallback)
 * @returns Total dollars spent in cents
 */
function getDollarsSpentFromMixedPriceCredits(paidCreditsUsedByPrice, paidCreditsUsed, currentPaidCreditPriceCents) {
    if (!paidCreditsUsedByPrice || Object.keys(paidCreditsUsedByPrice).length === 0) {
        return structure_1.AmountInCents.parse(paidCreditsUsed * currentPaidCreditPriceCents);
    }
    let totalCentsSpent = 0;
    let trackedCredits = 0;
    for (const [priceId, credits] of Object.entries(paidCreditsUsedByPrice)) {
        const priceInCents = (0, constants_1.getPaidCreditPriceInCents)(priceId);
        if (priceInCents !== undefined) {
            totalCentsSpent += credits * priceInCents;
            trackedCredits += credits;
        }
        else {
            totalCentsSpent += credits * currentPaidCreditPriceCents;
            trackedCredits += credits;
        }
    }
    const untrackedCredits = paidCreditsUsed - trackedCredits;
    if (untrackedCredits > 0) {
        totalCentsSpent += untrackedCredits * currentPaidCreditPriceCents;
    }
    return structure_1.AmountInCents.parse(totalCentsSpent);
}
/**
 * Calculate the remaining paid credits accounting for credits burned at different prices
 *
 * This function properly handles the case where a user has burned credits at different price points.
 * Instead of simply subtracting credit counts, it:
 * 1. Calculates the total dollars spent from all price tiers
 * 2. Calculates the remaining dollars (spending limit - dollars spent)
 * 3. Converts remaining dollars to credits at the current price
 *
 * @param effectiveSpendingLimit - The spending limit in cents
 * @param paidCreditsUsedByPrice - Map of Stripe price ID to credit amount burned at that price
 * @param paidCreditsUsed - Total paid credits used (fallback for legacy entries without price breakdown)
 * @param currentPaidCreditPriceCents - The current price per credit in cents
 * @returns Number of paid credits remaining
 */
function getPaidCreditsRemaining(effectiveSpendingLimit, paidCreditsUsedByPrice, paidCreditsUsed, currentPaidCreditPriceCents) {
    // -1 represents unlimited credits
    if (effectiveSpendingLimit === -1) {
        return -1;
    }
    if (!currentPaidCreditPriceCents) {
        return structure_2.CreditAmount.parse(0);
    }
    const dollarsSpent = getDollarsSpentFromMixedPriceCredits(paidCreditsUsedByPrice, paidCreditsUsed, currentPaidCreditPriceCents);
    const dollarsRemaining = Math.max(effectiveSpendingLimit - dollarsSpent, 0);
    const creditsRemaining = Math.floor(dollarsRemaining / currentPaidCreditPriceCents);
    return structure_2.CreditAmount.parse(creditsRemaining);
}
/**
 * Check if there are remaining paid credits accounting for mixed-price usage
 *
 * @param effectiveSpendingLimit - The spending limit in cents
 * @param paidCreditsUsedByPrice - Map of Stripe price ID to credit amount burned at that price
 * @param paidCreditsUsed - Total paid credits used (fallback for legacy entries without price breakdown)
 * @param currentPaidCreditPriceCents - The current price per credit in cents
 * @returns True if there are remaining paid credits
 */
function hasRemainingPaidCreditsWithMixedPrices(effectiveSpendingLimit, paidCreditsUsedByPrice, paidCreditsUsed, currentPaidCreditPriceCents) {
    if (effectiveSpendingLimit === -1) {
        return true;
    }
    const creditsRemaining = getPaidCreditsRemaining(effectiveSpendingLimit, paidCreditsUsedByPrice, paidCreditsUsed, currentPaidCreditPriceCents);
    return creditsRemaining > 0;
}
/**
 * Gets the value the effective spending limit (on the current period) should be set to when the account has used
 * some number of paid credits already at some price
 *
 * @param spendingLimit The spending limit the user wants in cents (from the account settings)
 * @param paidCreditsUsed The number of paid credits they've used already for this period
 * @param paidCreditPriceInCents The price per paid credit
 * @returns The effective spending limit in cents that should be stored on the current period summary
 */
function getEffectiveSpendingLimit(spendingLimit, paidCreditsUsed, paidCreditPriceInCents) {
    return structure_2.SpendingLimit.parse(Math.max(spendingLimit, paidCreditsUsed * paidCreditPriceInCents));
}
function creditsUsedFromLedgerEntry(ledgerEntry, logger) {
    const normalizedEntry = (0, structure_2.normalizeLedgerEntry)(ledgerEntry, (tokensUsed, tokenDivisors) => creditsUsedFromTokensUsed(tokensUsed, tokenDivisors, logger));
    const result = {};
    for (const appUsage of normalizedEntry.tokenUsageByApplication) {
        for (const [tokenType, credits] of Object.entries(appUsage.creditsUsed)) {
            const existing = result[tokenType] ?? structure_2.CreditAmount.parse(0);
            result[tokenType] = structure_2.CreditAmount.parse(existing + credits);
        }
    }
    return result;
}
function creditsUsedFromTokensUsed(tokensUsed, tokenDivisors, logger) {
    return (0, lodash_1.mapValues)(tokensUsed, (tokenAmount, tokenType) => {
        if (!tokenAmount) {
            return structure_2.CreditAmount.parse(0);
        }
        const divisor = structure_2.TokenToCreditDivisor.safeParse(tokenDivisors[tokenType]);
        if (!divisor.success) {
            logger.warn({
                tokenType,
                tokenAmount,
                error: divisor.error,
                message: "Could not find divisor for token type",
            });
            return structure_2.CreditAmount.parse(0);
        }
        return structure_2.CreditAmount.parse(tokenAmount / divisor.data);
    });
}
function totalCreditsUsed(creditUsage) {
    // Ceiling because e.g. a transaction that uses 0.9 credits is recorded as using 1 credit
    return structure_2.TotalCreditAmount.parse(Math.ceil((0, lodash_1.sum)(Object.values(creditUsage))));
}
/**
 * Get the default credit global settings
 *
 * Note that this shouldn't usually be used directly, because we merge in specific account settings from Firestore,
 * per account.
 *
 * See CreditSettingsRepository instead
 */
function getCreditGlobalSettings() {
    return (0, shared_1.areWeTestingWithJest)() ? constants_2.testCreditGlobalSettings : constants_2.defaultCreditGlobalSettings;
}
/**
 * Gets the first millisecond of the previous month from the given date
 *
 * @see https://stackoverflow.com/a/50098236
 */
function lastMonthUtc(from = new Date()) {
    from.setUTCDate(0); // Rolls backward to the last day of the previous month, even across years
    from.setUTCDate(1);
    return from;
}
/**
 * Gets the first millisecond of the next month, in UTC timezone, from the given date
 *
 * i.e. if it's the (localtime) second hour of the month in a +3 TZ, this will return the current local month (because
 * it's still the next month in UTC)
 */
function nextMonthUtc(from = new Date()) {
    // NB: month is 0...11
    if (from.getUTCMonth() === 11) {
        return new Date(Date.UTC(from.getUTCFullYear() + 1, 0, 1, 0, 0, 0, 0));
    }
    return new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth() + 1, 1, 0, 0, 0, 0));
}
function creditPeriodForDate(date) {
    if (isNaN(date.getTime())) {
        throw new Error(`Invalid date`);
    }
    return structure_2.TimeBasedCreditPeriod.parse(`p_${date.getUTCFullYear()}${(0, lodash_1.padStart)(String(date.getUTCMonth() + 1), 2, "0")}`);
}
/**
 * Get the current credit period from the given time
 */
function currentCreditPeriod(clock, from = new Date()) {
    if (clock === "stripe") {
        from = new Date(from.getTime() - structure_2.CreditPeriodOffsetForStripeMs);
    }
    return creditPeriodForDate(from);
}
/**
 * Get the previous credit period from the given time
 */
function previousCreditPeriod(clock, from = new Date()) {
    if (clock === "stripe") {
        from = new Date(from.getTime() - structure_2.CreditPeriodOffsetForStripeMs);
    }
    return creditPeriodForDate(lastMonthUtc(from));
}
/**
 * Get the next credit period from the given time
 */
function nextCreditPeriod(clock, from = new Date()) {
    if (clock === "stripe") {
        from = new Date(from.getTime() - structure_2.CreditPeriodOffsetForStripeMs);
    }
    return creditPeriodForDate(nextMonthUtc(from));
}
/**
 * Return the start and end dates of a given credit period
 */
function creditPeriodDates(clock, creditPeriod) {
    const year = Number.parseInt(creditPeriod.slice(2, 6));
    const month = Number.parseInt(creditPeriod.slice(6, 8));
    let start = new Date(Date.UTC(year, month - 1, 1, 0, 0, 0, 0));
    let end = new Date(nextMonthUtc(start).getTime() - 1);
    if (clock === "stripe") {
        start = new Date(start.getTime() + structure_2.CreditPeriodOffsetForStripeMs);
        end = new Date(end.getTime() + structure_2.CreditPeriodOffsetForStripeMs);
    }
    return { start, end };
}
function generateStaticCreditPeriod() {
    return structure_2.StaticCreditPeriod.parse(`scp_${(0, shared_1.guid)()}`);
}
/**
 * Whether the user requires consent to use paid credits, regardless of whether they can use paid credits, nor whether
 * they have already consented. This amounts to the "opt-in" flag for the feature.
 */
function getPaidCreditConsentRequired(metadata) {
    return (0, featureFlags_1.isExperimentActive)(metadata?.creationFlags?.paidCreditConsentRequired, featureFlags_1.ExperimentVariant.enum.off);
}
//# sourceMappingURL=functions.js.map